import { type Token, TokenTypes } from './formula.types';

/** Any string starting with whitespace characters. */
const WhitespaceAbsorptionRegex = /^\s+/;

/**
 * Any one of the following:
 * 1. A numeric string: An optional +/- followed by at least one digit, optionally followed by a . and some more digits.
 * 2. Any string of characters not containing whitespace, parens, braces, quotes, +, -, *, /, or comma.
 * 3. Any string of characters surrounded by {} or matching quotes, with \ used for escaping.
 */
const IdRegex =
  /[+-]\d+(?:\.\d*)?|[^\s(){}'"`+\-*/,]+|`(?:[^`]|\\`)+`|{[^}]+}|"(?:[^"]|\\")+"|'(?:[^']|\\')+'/g;

/** An equals sign. */
const EqualsRegex = /=/g;
/** Any of >, <, >=, or <=. */
const CompOpRegex = /[><]=?/g;
/** Any of /, *, or mod. */
const MulDivRegex = /[/*]|mod/gi;
/** Any + or -. */
const AddSubRegex = /[+-]/g;
/** An opening parens. */
const OpenParensRegex = /\(/g;
/** A closing parens. */
const CloseParensRegex = /\)/g;
/** A comma. */
const CommaRegex = /,/g;

/** Splits an input string into a set of tokens with their lexemes and starting/ending extents in the input. */
export function Lex(input: string) {
  /** Finds all matches for a regex in the input and packages them with their corresponding token. */
  function* getMatches(regex: RegExp, token: Token) {
    let match;

    while ((match = regex.exec(input))) {
      yield {
        token,
        lexeme: match[0],
        start: match.index,
        end:
          match.index +
          match[0].length +
          (input.slice(match.index + match[0].length).match(WhitespaceAbsorptionRegex)?.[0]
            ?.length ?? 0), // Absorb any trailing whitespace so that the end of one matches the start of the next.
      };
    }
  }

  const tokens = [];

  // Find all the tokens.
  for (const result of getMatches(IdRegex, TokenTypes.ID)) tokens.push(result);
  for (const result of getMatches(EqualsRegex, TokenTypes.EQUALS)) tokens.push(result);
  for (const result of getMatches(CompOpRegex, TokenTypes.COMPOP)) tokens.push(result);
  for (const result of getMatches(MulDivRegex, TokenTypes.MULDIVOP))
    tokens.push({ ...result, lexeme: result.lexeme.toLowerCase() }); // Normalise the lexeme here because `MOD` is case insensitive.
  for (const result of getMatches(AddSubRegex, TokenTypes.ADDSUBOP)) tokens.push(result);
  for (const result of getMatches(OpenParensRegex, TokenTypes.OPEN_PARENS)) tokens.push(result);
  for (const result of getMatches(CloseParensRegex, TokenTypes.CLOSE_PARENS)) tokens.push(result);
  for (const result of getMatches(CommaRegex, TokenTypes.COMMA)) tokens.push(result);

  const tweSet = tokens.reduce(
    (acc, item) => {
      if (item.start in acc) {
        acc[item.start]?.push(item);
      } else {
        acc[item.start] = [item];
      }

      return acc;
    },
    {} as Record<number, { token: Token; lexeme: string; start: number; end: number }[]>,
  );

  return tweSet;
}
